import React, { useEffect, useMemo, useRef, useState } from "react";
import { listIndustries } from "../services/analysisApi";

// Výběr oboru ze seznamu s vyhledáváním (název nebo kód NACE). Uživatelé kód obvykle neznají.
const norm = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

let cache = null;

export default function IndustryPicker({ value, onChange, placeholder = "Začněte psát obor, např. elektroinstalace" }) {
  const [items, setItems] = useState(cache || []);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    if (cache) return;
    listIndustries()
      .then((data) => {
        cache = data.industries || [];
        setItems(cache);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const selected = useMemo(() => items.find((i) => i.code === value), [items, value]);

  const results = useMemo(() => {
    const q = norm(query).trim();
    if (!q) return items.filter((i) => i.level === "l1").slice(0, 60);
    // čeština skloňuje („motory“ vs „motorů“) -> u delších slov stačí kmen bez koncovky
    const words = q.split(/\s+/).map((w) => (w.length > 4 ? w.slice(0, Math.max(4, w.length - 2)) : w));
    return items
      .filter((i) => {
        const hay = `${norm(i.name)} ${i.code}`;
        return words.every((w) => hay.includes(w));
      })
      .slice(0, 60);
  }, [items, query]);

  const choose = (item) => {
    onChange(item ? item.code : "");
    setQuery("");
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[active]) choose(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="ip" ref={boxRef}>
      {selected && !open ? (
        <div className="ip-selected">
          <span className="ip-code">{selected.code}</span>
          <span className="ip-name">{selected.name}</span>
          <button type="button" className="ip-clear" onClick={() => choose(null)} aria-label="Zrušit výběr oboru">×</button>
        </div>
      ) : (
        <input
          className="finanalyst-input"
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          role="combobox"
          aria-controls="industry-picker-list"
          aria-expanded={open}
          aria-autocomplete="list"
        />
      )}
      {!open && !selected && query.trim() && (
        <div className="ip-hint">Obor zatím není vybraný – klikněte na něj v seznamu.</div>
      )}
      {open && (
        <ul className="ip-list" role="listbox" id="industry-picker-list">
          {results.length === 0 && <li className="ip-empty">Nic nenalezeno</li>}
          {results.map((i, idx) => (
            <li
              key={i.code}
              role="option"
              aria-selected={idx === active}
              className={`ip-item ip-item--${i.level}${idx === active ? " is-active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(i);
              }}
              onMouseEnter={() => setActive(idx)}
            >
              <span className="ip-code">{i.code}</span>
              <span className="ip-name">{i.name}</span>
              <span className="ip-n">{i.n} firem</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
